import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tooltip,
  Snackbar,
  Alert,
  Chip,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Grid,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  Collapse,
  Divider,
  Paper
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { 
  Add, 
  Edit, 
  Delete, 
  Send, 
  Schedule, 
  Cancel, 
  Refresh, 
  ChevronLeft, 
  ChevronRight,
  Message,
  Warning,
  Info,
  CheckCircle,
  Error,
  Pause
} from '@mui/icons-material';
import { useTheme } from '../../context/ThemeContext';
import { systemMessageService } from '../../services/systemMessageService';
import { format } from 'date-fns';

const defaultForm = {
  title: '',
  message: '', // Changed from 'content' to 'message'
  message_type: 'notification', // Changed from 'general' to 'notification'
  priority: 1, // Changed from 'medium' to 1 (integer)
  target_audience: 'all',
  target_users: [],
  target_groups: [],
  delivery_methods: ['push', 'in_app'], // Changed from individual booleans
  send_immediately: true,
  scheduled_at: null, // Changed from empty string to null
  recurrence: 'once', // Changed from 'recurrence_type' to 'recurrence'
  recurrence_end_date: null, // Changed from empty string to null
  action_url: '',
  action_text: ''
};

const SystemMessages = () => {
  const { t } = useTranslation(['systemMessages', 'common']);
  const { currentTheme } = useTheme();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Delete confirmation modal state
  const [deleteModal, setDeleteModal] = useState({ open: false, messageId: null, messageTitle: '' });
  
  // Filter states
  const [filters, setFilters] = useState({
    status: '',
    message_type: '',
    priority: '',
    target_audience: ''
  });
  const [appliedFilters, setAppliedFilters] = useState({});
  const [isFiltering, setIsFiltering] = useState(false);

  // Pagination state
  const [page, setPage] = useState(0); // 0-based for frontend, 1-based for backend
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // Batch configuration state
  const [batchConfig, setBatchConfig] = useState({
    isOpen: false,
    batchSize: 100,
    userCount: 231,
    autoScaling: true,
    testResults: null
  });

  // Calculate total pages from totalCount
  const totalPages = Math.ceil(totalCount / rowsPerPage) || 1;

  // Fetch messages with pagination
  const fetchMessagesData = useCallback(async (pageArg, rowsArg, filtersArg) => {
    try {
      setLoading(true);
      const response = await systemMessageService.getMessages({
        ...filtersArg,
        page: pageArg + 1, // backend expects 1-based
        page_size: rowsArg,
        ordering: '-created_at',
      });
      
      setMessages(response.results || response.messages || []);
      setTotalCount(response.total_count || response.total || 0);
    } catch (err) {
      setError(err.message);
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMessagesData(page, rowsPerPage, appliedFilters);
  }, [page, rowsPerPage, appliedFilters, fetchMessagesData]);

  // Initialize batch configuration
  useEffect(() => {
    testBatchSize(batchConfig.batchSize);
  }, []);

  // Pagination handlers
  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };
  
  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };
  
  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(rowsPerPage.toString());
    } else {
      setRowsPerPage(Number(value));
      setPage(0);
    }
  };

  const handleCustomRowsInputChange = (e) => {
    setCustomRowsInput(e.target.value);
  };

  const handleCustomRowsInputBlur = () => {
    const value = parseInt(customRowsInput);
    if (value && value > 0) {
      setRowsPerPage(value);
      setPage(0);
    }
    setCustomRowsActive(false);
  };

  // Filter handlers - now with real-time filtering
  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    // Apply filters immediately
    setAppliedFilters(newFilters);
    setPage(0);
  };

  const clearFilters = () => {
    const emptyFilters = {
      status: '',
      message_type: '',
      priority: '',
      target_audience: ''
    };
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setPage(0);
  };

  const hasActiveFilters = Object.values(filters).some(value => value !== '');

  const handleRefresh = () => {
    fetchMessagesData(page, rowsPerPage, appliedFilters);
  };

  const handleOpen = () => {
    setOpen(true);
    setForm(defaultForm);
    setEditId(null);
  };

  const handleClose = () => {
    setOpen(false);
    setForm(defaultForm);
    setEditId(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let finalValue = value;
    if (type === 'checkbox') {
      finalValue = checked;
    }
    
    setForm({ 
      ...form, 
      [name]: finalValue
    });
  };

  const handleSave = async () => {
    if (!form.title || !form.message) {
      setSnackbar({ open: true, message: t('systemMessages:messages.titleAndMessageRequired'), severity: 'error' });
      return;
    }

    // Prepare form data for API
    const formData = {
      title: form.title,
      message: form.message,
      message_type: form.message_type,
      priority: form.priority,
      target_audience: form.target_audience,
      target_users: form.target_users,
      target_groups: form.target_groups,
      delivery_methods: form.delivery_methods,
      send_immediately: form.send_immediately,
      scheduled_at: form.scheduled_at || null,
      recurrence: form.recurrence,
      recurrence_end_date: form.recurrence_end_date || null,
      action_url: form.action_url,
      action_text: form.action_text
    };

    try {
      if (editId) {
        await systemMessageService.updateMessage(editId, formData);
        setSnackbar({ open: true, message: t('systemMessages:messages.updatedSuccess'), severity: 'success' });
      } else {
        await systemMessageService.createMessage(formData);
        setSnackbar({ open: true, message: t('systemMessages:messages.createdSuccess'), severity: 'success' });
      }
      handleClose();
      fetchMessagesData(page, rowsPerPage, appliedFilters);
    } catch (error) {
      console.error('Error saving message:', error);
      setSnackbar({ open: true, message: error.message || t('systemMessages:messages.errorSaving'), severity: 'error' });
    }
  };

  const handleDelete = async (id) => {
    try {
      await systemMessageService.deleteMessage(id);
      setSnackbar({ open: true, message: t('systemMessages:messages.deletedSuccess'), severity: 'info' });
      setDeleteModal({ open: false, messageId: null, messageTitle: '' });
      fetchMessagesData(page, rowsPerPage, appliedFilters);
    } catch (error) {
      setSnackbar({ open: true, message: error.message || t('systemMessages:messages.errorDeleting'), severity: 'error' });
    }
  };

  const handleDeleteClick = (message) => {
    setDeleteModal({
      open: true,
      messageId: message.id,
      messageTitle: message.title
    });
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ open: false, messageId: null, messageTitle: '' });
  };

  const handleDeleteConfirm = () => {
    if (deleteModal.messageId) {
      handleDelete(deleteModal.messageId);
    }
  };

  const handleSendMessage = async (messageId) => {
    try {
      // Send with batch size configuration
      const sendData = {
        batch_size: batchConfig.batchSize,
        use_async: true
      };
      await systemMessageService.sendMessage(messageId, sendData);
      setSnackbar({ open: true, message: t('systemMessages:messages.sentSuccess', { size: batchConfig.batchSize }), severity: 'success' });
      fetchMessagesData(page, rowsPerPage, appliedFilters);
    } catch (error) {
      setSnackbar({ open: true, message: error.message || t('systemMessages:messages.errorSending'), severity: 'error' });
    }
  };

  const handleScheduleMessage = async (messageId) => {
    try {
      await systemMessageService.scheduleMessage(messageId);
      setSnackbar({ open: true, message: t('systemMessages:messages.scheduledSuccess'), severity: 'success' });
      fetchMessagesData(page, rowsPerPage, appliedFilters);
    } catch (error) {
      setSnackbar({ open: true, message: error.message || t('systemMessages:messages.errorScheduling'), severity: 'error' });
    }
  };

  const handleCancelMessage = async (messageId) => {
    try {
      await systemMessageService.cancelMessage(messageId);
      setSnackbar({ open: true, message: t('systemMessages:messages.cancelledSuccess'), severity: 'success' });
      fetchMessagesData(page, rowsPerPage, appliedFilters);
    } catch (error) {
      setSnackbar({ open: true, message: error.message || t('systemMessages:messages.errorCancelling'), severity: 'error' });
    }
  };

  // Batch configuration functions
  const toggleBatchConfig = () => {
    setBatchConfig(prev => ({ ...prev, isOpen: !prev.isOpen }));
  };

  const handleBatchSizeChange = (size) => {
    setBatchConfig(prev => ({ ...prev, batchSize: size }));
    testBatchSize(size);
  };

  const testBatchSize = (size = batchConfig.batchSize) => {
    const userCount = batchConfig.userCount;
    const estimatedBatches = Math.ceil(userCount / size);
    const estimatedTimeMinutes = Math.round((userCount / size) * 0.1 * 10) / 10;
    
    // Get recommendation
    let recommendedSize = 100;
    if (userCount > 100000) recommendedSize = 5000;
    else if (userCount > 50000) recommendedSize = 2000;
    else if (userCount > 10000) recommendedSize = 1000;
    else if (userCount > 1000) recommendedSize = 500;
    
    // Calculate efficiency
    let efficiency = 'acceptable';
    let efficiencyColor = '#757575';
    
    if (size === recommendedSize) {
      efficiency = 'optimal';
      efficiencyColor = '#4CAF50';
    } else if (Math.abs(size - recommendedSize) <= recommendedSize * 0.1) {
      efficiency = 'good';
      efficiencyColor = '#2196F3';
    } else if (size < recommendedSize * 0.5) {
      efficiency = 'too small';
      efficiencyColor = '#FF9800';
    } else if (size > recommendedSize * 2) {
      efficiency = 'too large';
      efficiencyColor = '#F44336';
    }
    
    setBatchConfig(prev => ({
      ...prev,
      testResults: {
        efficiency,
        efficiencyColor,
        estimatedBatches,
        estimatedTimeMinutes,
        recommendedSize
      }
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return '#757575';
      case 'scheduled': return '#FF9800';
      case 'sending': return '#2196F3';
      case 'sent': return '#4CAF50';
      case 'failed': return '#F44336';
      case 'cancelled': return '#9E9E9E';
      default: return '#757575';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'draft': return <Edit />;
      case 'scheduled': return <Schedule />;
      case 'sending': return <Send />;
      case 'sent': return <CheckCircle />;
      case 'failed': return <Error />;
      case 'cancelled': return <Cancel />;
      default: return <Message />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 1:
      case 'low': return '#4CAF50';
      case 2:
      case 'medium': return '#FF9800';
      case 3:
      case 'high': return '#F44336';
      case 4:
      case 'urgent': return '#E91E63';
      default: return '#757575';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 1: return t('systemMessages:priority.low');
      case 2: return t('systemMessages:priority.medium');
      case 3: return t('systemMessages:priority.high');
      case 4: return t('systemMessages:priority.critical');
      case 'low': return t('systemMessages:priority.low');
      case 'medium': return t('systemMessages:priority.medium');
      case 'high': return t('systemMessages:priority.high');
      case 'urgent': return t('systemMessages:priority.critical');
      default: return t('systemMessages:priority.unknown');
    }
  };

  const getMessageTypeColor = (type) => {
    switch (type) {
      case 'announcement': return '#2196F3';
      case 'alert': return '#F44336';
      case 'update': return '#00BCD4';
      case 'maintenance': return '#FF9800';
      case 'promotion': return '#4CAF50';
      case 'security': return '#9C27B0';
      case 'general': return '#757575';
      default: return '#757575';
    }
  };

  // Add serial numbers to messages for the current page
  const addSerialNumbers = (data, page, rowsPerPage) =>
    data.map((row, idx) => ({
      ...row,
      sn: (page * rowsPerPage) + idx + 1
    }));

  const columns = [
    { field: 'sn', headerName: t('systemMessages:table.sn'), width: 80, sortable: false, filterable: false },

    { 
      field: 'title', 
      headerName: t('systemMessages:table.title'), 
      width: 200,
      renderCell: (params) => {
        const title = params.row?.title || 'N/A';
        return (
          <Typography variant="body2" fontWeight="bold">
            {title}
          </Typography>
        );
      }
    },

    { 
      field: 'message', 
      headerName: t('systemMessages:table.message'), 
      flex: 2, 
      minWidth: 250,
      renderCell: (params) => {
        const message = params.row?.message || '';
        const truncatedMessage = message.length > 150 ? `${message.substring(0, 150)}...` : message;
        
        return (
          <Typography variant="body2" color="textSecondary">
            {truncatedMessage}
          </Typography>
        );
      }
    },

    { 
      field: 'message_type', 
      headerName: t('systemMessages:table.type'), 
      width: 120,
      renderCell: (params) => {
        const type = params.row?.message_type || 'general';
        return (
          <Chip
            label={type.charAt(0).toUpperCase() + type.slice(1)}
            size="small"
            sx={{ 
              backgroundColor: getMessageTypeColor(type),
              color: 'white',
              fontWeight: 'bold'
            }}
          />
        );
      }
    },

    { 
      field: 'priority', 
      headerName: t('systemMessages:table.priority'), 
      width: 100,
      renderCell: (params) => {
        const priority = params.row?.priority || 2;
        return (
          <Chip
            label={getPriorityLabel(priority)}
            size="small"
            sx={{ 
              backgroundColor: getPriorityColor(priority),
              color: 'white',
              fontWeight: 'bold'
            }}
          />
        );
      }
    },

    { 
      field: 'status', 
      headerName: t('systemMessages:table.status'), 
      width: 120,
      renderCell: (params) => {
        const status = params.row?.status || 'draft';
        return (
          <Chip
            icon={getStatusIcon(status)}
            label={status.charAt(0).toUpperCase() + status.slice(1)}
            size="small"
            sx={{ 
              backgroundColor: getStatusColor(status),
              color: 'white',
              fontWeight: 'bold'
            }}
          />
        );
      }
    },

    { 
      field: 'target_audience', 
      headerName: t('systemMessages:table.target'), 
      width: 120,
      renderCell: (params) => {
        const audience = params.row?.target_audience || 'all';
        const audienceLabels = {
          all: t('systemMessages:targetAudience.all'),
          active: t('systemMessages:targetAudience.active'),
          premium: t('systemMessages:targetAudience.premium'),
          specific: t('systemMessages:targetAudience.specific'),
          group: t('systemMessages:targetAudience.group')
        };
        return (
          <Chip
            label={audienceLabels[audience] || audience}
            size="small"
            color="info"
            variant="outlined"
          />
        );
      }
    },

    { 
      field: 'recipients', 
      headerName: t('systemMessages:table.recipients'), 
      width: 120,
      renderCell: (params) => {
        const delivered = params.row?.delivered_count || 0;
        const total = params.row?.total_recipients || 0;
        const rate = params.row?.delivery_rate || 0;
        
        return (
          <Box>
            <Typography variant="body2" fontWeight="bold">
              {delivered} / {total}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {rate.toFixed(1)}% {t('systemMessages:table.delivered')}
            </Typography>
          </Box>
        );
      }
    },

    { 
      field: 'created_at', 
      headerName: t('systemMessages:table.created'), 
      width: 150,
      renderCell: (params) => {
        const date = params.row?.created_at;
        if (!date) return 'N/A';
        
        const createdDate = new Date(date);
        return (
          <Box>
            <Typography variant="body2">
              {format(createdDate, 'MMM dd, yyyy')}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {format(createdDate, 'HH:mm')}
            </Typography>
          </Box>
        );
      }
    },

    {
      field: 'actions',
      headerName: t('systemMessages:table.actions'),
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const message = params.row;
        
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title={t('systemMessages:tooltips.edit')}>
              <IconButton
                size="small"
                onClick={() => {
                  setEditId(message.id);
                  setForm({
                    title: message.title,
                    message: message.message,
                    message_type: message.message_type,
                    priority: message.priority,
                    target_audience: message.target_audience,
                    target_users: message.target_users || [],
                    target_groups: message.target_groups || [],
                    delivery_methods: message.delivery_methods || ['push', 'in_app'],
                    send_immediately: message.send_immediately !== false,
                    scheduled_at: message.scheduled_at,
                    recurrence: message.recurrence || 'once',
                    recurrence_end_date: message.recurrence_end_date,
                    action_url: message.action_url || '',
                    action_text: message.action_text || ''
                  });
                  setOpen(true);
                }}
              >
                <Edit />
              </IconButton>
            </Tooltip>

            {message.status === 'draft' && (
              <>
                <Tooltip title={t('systemMessages:tooltips.sendNow', { size: batchConfig.batchSize })}>
                  <IconButton
                    size="small"
                    onClick={() => handleSendMessage(message.id)}
                    color="success"
                  >
                    <Send />
                  </IconButton>
                </Tooltip>
                {message.is_scheduled && (
                  <Tooltip title={t('systemMessages:tooltips.schedule')}>
                    <IconButton
                      size="small"
                      onClick={() => handleScheduleMessage(message.id)}
                      color="warning"
                    >
                      <Schedule />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}

            {(message.status === 'scheduled' || message.status === 'sending') && (
              <Tooltip title={t('systemMessages:tooltips.cancel')}>
                <IconButton
                  size="small"
                  onClick={() => handleCancelMessage(message.id)}
                  color="error"
                >
                  <Cancel />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title={t('systemMessages:tooltips.delete')}>
              <IconButton
                size="small"
                onClick={() => handleDeleteClick(message)}
                color="error"
              >
                <Delete />
              </IconButton>
            </Tooltip>
          </Box>
        );
      }
    }
  ];

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
          {t('systemMessages:title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpen}
          sx={{ 
            backgroundColor: '#DC143C', 
            '&:hover': { backgroundColor: '#B91C3C' },
            minWidth: 'auto'
          }}
        >
          {t('systemMessages:createMessage')}
        </Button>
      </Box>

      {/* Batch Size Configuration Panel */}
      <Card sx={{ mb: 3, backgroundColor: 'background.paper' }}>
        <CardContent>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 2,
            flexWrap: 'wrap',
            gap: 2
          }}>
            <Box>
              <Typography variant="h6" sx={{ color: '#DC143C', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Message />
                {t('systemMessages:batchConfiguration.title')}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {t('systemMessages:batchConfiguration.description')}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              onClick={toggleBatchConfig}
              sx={{ color: '#DC143C', borderColor: '#DC143C', minWidth: 'auto' }}
            >
              {batchConfig.isOpen ? t('systemMessages:batchConfiguration.hideConfiguration') : t('systemMessages:batchConfiguration.showConfiguration')}
            </Button>
          </Box>
          
          <Collapse in={batchConfig.isOpen}>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 8 }}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {t('systemMessages:batchConfiguration.currentUserCount')} <strong>{batchConfig.userCount.toLocaleString()}</strong>
                  </Typography>
                  
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {t('systemMessages:batchConfiguration.quickPresets')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {[
                      { size: 100, label: t('systemMessages:batchConfiguration.presets.small'), desc: t('systemMessages:batchConfiguration.presets.smallDesc') },
                      { size: 500, label: t('systemMessages:batchConfiguration.presets.medium'), desc: t('systemMessages:batchConfiguration.presets.mediumDesc') },
                      { size: 1000, label: t('systemMessages:batchConfiguration.presets.large'), desc: t('systemMessages:batchConfiguration.presets.largeDesc') },
                      { size: 2000, label: t('systemMessages:batchConfiguration.presets.xlarge'), desc: t('systemMessages:batchConfiguration.presets.xlargeDesc') },
                      { size: 5000, label: t('systemMessages:batchConfiguration.presets.xxlarge'), desc: t('systemMessages:batchConfiguration.presets.xxlargeDesc') }
                    ].map((preset) => (
                      <Chip
                        key={preset.size}
                        label={preset.label}
                        onClick={() => handleBatchSizeChange(preset.size)}
                        variant={batchConfig.batchSize === preset.size ? 'filled' : 'outlined'}
                        color={batchConfig.batchSize === preset.size ? 'primary' : 'default'}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#e3f2fd' }
                        }}
                      />
                    ))}
                  </Box>
                  
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label={t('systemMessages:batchConfiguration.customBatchSize')}
                        type="number"
                        value={batchConfig.batchSize}
                        onChange={(e) => handleBatchSizeChange(parseInt(e.target.value) || 100)}
                        inputProps={{ min: 10, max: 10000 }}
                        size="small"
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={batchConfig.autoScaling}
                            onChange={(e) => setBatchConfig(prev => ({ ...prev, autoScaling: e.target.checked }))}
                          />
                        }
                        label={t('systemMessages:batchConfiguration.autoScaling')}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              
              <Grid size={{ xs: 12, md: 4 }}>
                {batchConfig.testResults && (
                  <Card sx={{ p: 2, backgroundColor: 'background.paper' }}>
                    <Typography variant="h6" sx={{ color: '#DC143C', mb: 2 }}>
                      {t('systemMessages:batchConfiguration.testResults.title')}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" fontWeight="bold">{t('systemMessages:batchConfiguration.testResults.efficiency')}</Typography>
                        <Chip
                          label={batchConfig.testResults.efficiency}
                          size="small"
                          sx={{ 
                            backgroundColor: batchConfig.testResults.efficiencyColor,
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" fontWeight="bold">{t('systemMessages:batchConfiguration.testResults.estimatedBatches')}</Typography>
                        <Typography variant="body2">{batchConfig.testResults.estimatedBatches.toLocaleString()}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" fontWeight="bold">{t('systemMessages:batchConfiguration.testResults.estimatedTime')}</Typography>
                        <Typography variant="body2">{batchConfig.testResults.estimatedTimeMinutes} {t('systemMessages:batchConfiguration.testResults.minutes')}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" fontWeight="bold">{t('systemMessages:batchConfiguration.testResults.recommended')}</Typography>
                        <Typography variant="body2">{batchConfig.testResults.recommendedSize.toLocaleString()} {t('systemMessages:batchConfiguration.testResults.users')}</Typography>
                      </Box>
                    </Box>
                  </Card>
                )}
              </Grid>
            </Grid>
          </Collapse>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card sx={{ mb: 3, backgroundColor: 'background.paper' }}>
        <CardContent>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 2,
            flexWrap: 'wrap',
            gap: 2
          }}>
            <Box>
              <Typography variant="h6" sx={{ color: '#DC143C', fontWeight: 'bold' }}>
                {t('systemMessages:filters.title')}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {t('systemMessages:filters.description')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="textSecondary">
                {t('systemMessages:filters.showingMessages', { count: messages.length })}
                {Object.keys(appliedFilters).length > 0 && (
                  <span> {t('systemMessages:filters.filtered')}</span>
                )}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={clearFilters}
                disabled={Object.keys(appliedFilters).length === 0 && !hasActiveFilters}
                sx={{ minWidth: 'auto' }}
              >
                {t('systemMessages:filters.clearFilters')}
              </Button>
              <IconButton onClick={handleRefresh} disabled={loading}>
                <Refresh />
              </IconButton>
            </Box>
          </Box>
        
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('systemMessages:filters.status')}</InputLabel>
                <Select
                  value={filters.status}
                  label={t('systemMessages:filters.status')}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="">{t('systemMessages:filters.allStatus')}</MenuItem>
                  <MenuItem value="draft">{t('systemMessages:status.draft')}</MenuItem>
                  <MenuItem value="scheduled">{t('systemMessages:status.scheduled')}</MenuItem>
                  <MenuItem value="sending">{t('systemMessages:status.sending')}</MenuItem>
                  <MenuItem value="sent">{t('systemMessages:status.sent')}</MenuItem>
                  <MenuItem value="failed">{t('systemMessages:status.failed')}</MenuItem>
                  <MenuItem value="cancelled">{t('systemMessages:status.cancelled')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('systemMessages:filters.messageType')}</InputLabel>
                <Select
                  value={filters.message_type}
                  label={t('systemMessages:filters.messageType')}
                  onChange={(e) => handleFilterChange('message_type', e.target.value)}
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="">{t('systemMessages:filters.allTypes')}</MenuItem>
                  <MenuItem value="notification">{t('systemMessages:messageType.notification')}</MenuItem>
                  <MenuItem value="alert">{t('systemMessages:messageType.alert')}</MenuItem>
                  <MenuItem value="maintenance">{t('systemMessages:messageType.maintenance')}</MenuItem>
                  <MenuItem value="promotion">{t('systemMessages:messageType.promotion')}</MenuItem>
                  <MenuItem value="update">{t('systemMessages:messageType.update')}</MenuItem>
                  <MenuItem value="warning">{t('systemMessages:messageType.warning')}</MenuItem>
                  <MenuItem value="info">{t('systemMessages:messageType.info')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('systemMessages:filters.priority')}</InputLabel>
                <Select
                  value={filters.priority}
                  label={t('systemMessages:filters.priority')}
                  onChange={(e) => handleFilterChange('priority', e.target.value)}
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="">{t('systemMessages:filters.allPriorities')}</MenuItem>
                  <MenuItem value="1">{t('systemMessages:priority.lowValue')}</MenuItem>
                  <MenuItem value="2">{t('systemMessages:priority.mediumValue')}</MenuItem>
                  <MenuItem value="3">{t('systemMessages:priority.highValue')}</MenuItem>
                  <MenuItem value="4">{t('systemMessages:priority.criticalValue')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('systemMessages:filters.targetAudience')}</InputLabel>
                <Select
                  value={filters.target_audience}
                  label={t('systemMessages:filters.targetAudience')}
                  onChange={(e) => handleFilterChange('target_audience', e.target.value)}
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="">{t('systemMessages:filters.allAudiences')}</MenuItem>
                  <MenuItem value="all">{t('systemMessages:targetAudience.all')}</MenuItem>
                  <MenuItem value="active">{t('systemMessages:targetAudience.active')}</MenuItem>
                  <MenuItem value="premium">{t('systemMessages:targetAudience.premium')}</MenuItem>
                  <MenuItem value="specific">{t('systemMessages:targetAudience.specific')}</MenuItem>
                  <MenuItem value="group">{t('systemMessages:targetAudience.group')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      {/* DataGrid */}
      <Card sx={{ backgroundColor: 'background.paper' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{
            width: '100%',
            overflowX: 'auto',
            minHeight: 520,
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.1)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: 'rgba(0,0,0,0.5)',
            },
          }}>
            {messages.length === 0 && !loading && !isFiltering ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column' }}>
                <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
                  {t('systemMessages:messages.noMessagesFound')}
                </Typography>
                <Button variant="outlined" onClick={handleRefresh}>
                  {t('systemMessages:messages.refreshData')}
                </Button>
              </Box>
            ) : (
              <DataGrid
                rows={addSerialNumbers(messages, page, rowsPerPage)}
                columns={columns}
                pageSize={rowsPerPage}
                rowsPerPageOptions={[]}
                disableSelectionOnClick
                loading={loading || isFiltering}
                hideFooter
                sx={{
                  border: 'none',
                  minWidth: 1200, // Ensure minimum width for all columns
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: '#DC143C',
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                    color: 'white'
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: '#FFF0F3'
                  },
                  '& .MuiDataGrid-cell': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    padding: { xs: '8px 4px', sm: '8px 12px' }
                  }
                }}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Custom Pagination Bar */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: { xs: 'center', sm: 'flex-end' }, 
        mt: 3, 
        gap: 2,
        flexWrap: 'wrap'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handlePrevPage} disabled={page === 0}>
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2">
            {t('systemMessages:pagination.page', { current: totalPages === 0 ? 0 : page + 1, total: totalPages })}
          </Typography>
          <IconButton onClick={handleNextPage} disabled={page >= totalPages - 1}>
            <ChevronRight />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">
            {t('systemMessages:pagination.rowsPerPage')}
          </Typography>
          {!customRowsActive ? (
            <TextField
              select
              size="small"
              value={[10, 20, 50, 100].includes(rowsPerPage) ? rowsPerPage : 'custom'}
              onChange={handleRowsPerPageChange}
              sx={{ width: 100 }}
            >
              {[10, 20, 50, 100].map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
              <MenuItem value="custom">{t('systemMessages:pagination.custom')}</MenuItem>
            </TextField>
          ) : (
            <TextField
              size="small"
              value={customRowsInput}
              onChange={handleCustomRowsInputChange}
              onBlur={handleCustomRowsInputBlur}
              autoFocus
              sx={{ width: 100 }}
              inputProps={{ min: 1, style: { textAlign: 'center' } }}
              placeholder={t('systemMessages:pagination.rows')}
            />
          )}
        </Box>
      </Box>

      {/* Create/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: '#DC143C', fontWeight: 'bold' }}>
          {editId ? t('systemMessages:dialogs.create.editTitle') : t('systemMessages:dialogs.create.title')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label={t('systemMessages:dialogs.create.titleLabel')}
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  required
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('systemMessages:dialogs.create.messageTypeLabel')}</InputLabel>
                  <Select
                    name="message_type"
                    value={form.message_type}
                    label={t('systemMessages:dialogs.create.messageTypeLabel')}
                    onChange={handleChange}
                    required
                    sx={{ minWidth: 200 }}
                  >
                    <MenuItem value="notification">{t('systemMessages:messageType.notification')}</MenuItem>
                    <MenuItem value="alert">{t('systemMessages:messageType.alert')}</MenuItem>
                    <MenuItem value="maintenance">{t('systemMessages:messageType.maintenance')}</MenuItem>
                    <MenuItem value="promotion">{t('systemMessages:messageType.promotion')}</MenuItem>
                    <MenuItem value="update">{t('systemMessages:messageType.update')}</MenuItem>
                    <MenuItem value="warning">{t('systemMessages:messageType.warning')}</MenuItem>
                    <MenuItem value="info">{t('systemMessages:messageType.info')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('systemMessages:dialogs.create.priorityLabel')}</InputLabel>
                  <Select
                    name="priority"
                    value={form.priority}
                    label={t('systemMessages:dialogs.create.priorityLabel')}
                    onChange={handleChange}
                    required
                    sx={{ minWidth: 200 }}
                  >
                    <MenuItem value={1}>{t('systemMessages:priority.lowValue')}</MenuItem>
                    <MenuItem value={2}>{t('systemMessages:priority.mediumValue')}</MenuItem>
                    <MenuItem value={3}>{t('systemMessages:priority.highValue')}</MenuItem>
                    <MenuItem value={4}>{t('systemMessages:priority.criticalValue')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('systemMessages:dialogs.create.targetAudienceLabel')}</InputLabel>
                  <Select
                    name="target_audience"
                    value={form.target_audience}
                    label={t('systemMessages:dialogs.create.targetAudienceLabel')}
                    onChange={handleChange}
                    required
                    sx={{ minWidth: 200 }}
                  >
                    <MenuItem value="all">{t('systemMessages:targetAudience.all')}</MenuItem>
                    <MenuItem value="active">{t('systemMessages:targetAudience.activeOnly')}</MenuItem>
                    <MenuItem value="premium">{t('systemMessages:targetAudience.premiumOnly')}</MenuItem>
                    <MenuItem value="specific">{t('systemMessages:targetAudience.specific')}</MenuItem>
                    <MenuItem value="group">{t('systemMessages:targetAudience.group')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <TextField
              label={t('systemMessages:dialogs.create.messageLabel')}
              name="message"
              value={form.message}
              onChange={handleChange}
              multiline
              rows={4}
              required
              fullWidth
            />

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t('systemMessages:dialogs.create.deliveryMethods')}
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.delivery_methods.includes('push')}
                      onChange={(e) => {
                        const methods = e.target.checked 
                          ? [...form.delivery_methods, 'push']
                          : form.delivery_methods.filter(m => m !== 'push');
                        setForm({ ...form, delivery_methods: methods });
                      }}
                    />
                  }
                  label={t('systemMessages:dialogs.create.pushNotifications')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.delivery_methods.includes('in_app')}
                      onChange={(e) => {
                        const methods = e.target.checked 
                          ? [...form.delivery_methods, 'in_app']
                          : form.delivery_methods.filter(m => m !== 'in_app');
                        setForm({ ...form, delivery_methods: methods });
                      }}
                    />
                  }
                  label={t('systemMessages:dialogs.create.inAppNotifications')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.delivery_methods.includes('email')}
                      onChange={(e) => {
                        const methods = e.target.checked 
                          ? [...form.delivery_methods, 'email']
                          : form.delivery_methods.filter(m => m !== 'email');
                        setForm({ ...form, delivery_methods: methods });
                      }}
                    />
                  }
                  label={t('systemMessages:dialogs.create.email')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.delivery_methods.includes('sms')}
                      onChange={(e) => {
                        const methods = e.target.checked 
                          ? [...form.delivery_methods, 'sms']
                          : form.delivery_methods.filter(m => m !== 'sms');
                        setForm({ ...form, delivery_methods: methods });
                      }}
                    />
                  }
                  label={t('systemMessages:dialogs.create.sms')}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!form.send_immediately}
                      onChange={(e) => setForm({ ...form, send_immediately: !e.target.checked })}
                    />
                  }
                  label={t('systemMessages:dialogs.create.scheduleForLater')}
                />

                {!form.send_immediately && (
                  <>
                    <TextField
                      label={t('systemMessages:dialogs.create.scheduledTime')}
                      name="scheduled_at"
                      type="datetime-local"
                      value={form.scheduled_at ? format(new Date(form.scheduled_at), "yyyy-MM-dd'T'HH:mm") : ''}
                      onChange={(e) => setForm({ ...form, scheduled_at: e.target.value ? new Date(e.target.value).toISOString() : null })}
                      fullWidth
                      sx={{ mt: 2 }}
                      InputLabelProps={{ shrink: true }}
                    />

                    <FormControl fullWidth sx={{ mt: 2 }}>
                      <InputLabel>{t('systemMessages:dialogs.create.recurrence')}</InputLabel>
                      <Select
                        name="recurrence"
                        value={form.recurrence}
                        label={t('systemMessages:dialogs.create.recurrence')}
                        onChange={handleChange}
                        sx={{ minWidth: 200 }}
                      >
                        <MenuItem value="once">{t('systemMessages:dialogs.create.recurrenceOptions.once')}</MenuItem>
                        <MenuItem value="daily">{t('systemMessages:dialogs.create.recurrenceOptions.daily')}</MenuItem>
                        <MenuItem value="weekly">{t('systemMessages:dialogs.create.recurrenceOptions.weekly')}</MenuItem>
                        <MenuItem value="monthly">{t('systemMessages:dialogs.create.recurrenceOptions.monthly')}</MenuItem>
                      </Select>
                    </FormControl>

                    {form.recurrence !== 'once' && (
                      <TextField
                        label={t('systemMessages:dialogs.create.endDate')}
                        name="recurrence_end_date"
                        type="datetime-local"
                        value={form.recurrence_end_date ? format(new Date(form.recurrence_end_date), "yyyy-MM-dd'T'HH:mm") : ''}
                        onChange={(e) => setForm({ ...form, recurrence_end_date: e.target.value ? new Date(e.target.value).toISOString() : null })}
                        fullWidth
                        sx={{ mt: 2 }}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  </>
                )}
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('systemMessages:dialogs.create.cancel')}</Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            {editId ? t('systemMessages:dialogs.create.update') : t('systemMessages:dialogs.create.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteModal.open} onClose={handleDeleteCancel}>
        <DialogTitle>{t('systemMessages:dialogs.delete.title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('systemMessages:dialogs.delete.message', { title: deleteModal.messageTitle })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>{t('systemMessages:dialogs.delete.cancel')}</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            {t('systemMessages:dialogs.delete.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SystemMessages;
